import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { useAuth } from '../auth/auth-store';
import { MemberSelect } from '../ui/UserSelect';
import { searchEntity } from '../crm/api';
import { startManaging } from './api';

/**
 * Putting a company under management.
 *
 * The company already exists — this is not a place to create one. Customer success starts when
 * somebody who bought something becomes somebody's responsibility, and the field that matters is the
 * person, not the paperwork. MRR and the renewal date are optional because Phase 27 will fill both
 * from a signed contract, and a form that demands them today is a form people put off.
 */
export function NewAccountDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): React.JSX.Element | null {
  const dialog = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const client = useQueryClient();
  const { user } = useAuth();

  const [companyId, setCompanyId] = useState('');
  const [csmId, setCsmId] = useState('');
  const [mrr, setMrr] = useState('');
  const [renewalDate, setRenewalDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const companies = useQuery({
    queryKey: ['cs-companies'],
    queryFn: () =>
      searchEntity<{ id: string; name: string }>('companies', {
        sort: { field: 'name', direction: 'asc' },
        limit: 200,
      }),
    enabled: open,
  });

  useEffect(() => {
    const element = dialog.current;

    if (!element) return;

    if (open && !element.open) {
      setCompanyId('');
      setCsmId(user?.id ?? '');
      setMrr('');
      setRenewalDate('');
      setError(null);
      element.showModal();
    }

    if (!open && element.open) element.close();
  }, [open, user?.id]);

  const create = useMutation({
    mutationFn: () =>
      startManaging({
        companyId,
        ...(csmId ? { csmId } : {}),
        // Typed in reais, stored in centavos. One conversion, at the edge.
        ...(mrr.trim() ? { mrrCents: Math.round(Number(mrr.replace(',', '.')) * 100) } : {}),
        ...(renewalDate ? { renewalDate } : {}),
      }),
    onSuccess: (account) => {
      void client.invalidateQueries({ queryKey: ['cs-accounts'] });
      void client.invalidateQueries({ queryKey: ['cs-summary'] });
      onClose();
      navigate(`/sucesso/contas/${account.id}`);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (!open) return null;

  return (
    <dialog ref={dialog} className="confirm confirm--wide" onCancel={onClose} onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          if (companyId) create.mutate();
        }}
      >
        <h2>{t('cs.manageCompany')}</h2>
        <p className="muted">{t('cs.manageHint')}</p>

        <label className="stack__field">
          <span>
            {t('cs.company')} <span className="required">*</span>
          </span>
          <select
            className="select"
            value={companyId}
            autoFocus
            onChange={(event) => setCompanyId(event.target.value)}
          >
            <option value="">{t('cs.pickCompany')}</option>
            {(companies.data?.results ?? []).map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </label>

        <div className="stack__field">
          <span>{t('cs.csm')}</span>
          <MemberSelect
            value={csmId || null}
            placeholder={t('cs.noCsm')}
            onChange={(next) => setCsmId(next ?? '')}
          />
        </div>

        <div className="form-grid">
          <label className="stack__field">
            <span>{t('cs.mrrMonthly')}</span>
            <input
              inputMode="decimal"
              value={mrr}
              placeholder="0,00"
              onChange={(event) => setMrr(event.target.value)}
            />
          </label>

          <label className="stack__field">
            <span>{t('cs.renewalDate')}</span>
            <input
              type="date"
              value={renewalDate}
              onChange={(event) => setRenewalDate(event.target.value)}
            />
          </label>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="confirm__actions">
          <button type="button" className="button button--quiet button--compact" onClick={onClose}>
            {t('confirm.cancel')}
          </button>

          <button
            type="submit"
            className="button button--primary button--compact"
            disabled={companyId === '' || create.isPending}
          >
            {create.isPending ? t('work.creating') : t('cs.startManaging')}
          </button>
        </div>
      </form>
    </dialog>
  );
}
